'use client'

import React, { useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import {
  trackAndroidWaitlistRedirect,
  trackAppStoreClick,
  trackCtaImpression,
  type EventParams,
} from '@/utils/analytics'
import { classifyAppStorePlatform } from '@/utils/appStoreTracking'

/**
 * Centralized App Store link.
 *
 * Every clickable App Store CTA on the site routes through this component so
 * we get one canonical URL + one `app_store_click` event per click, fired to
 * BOTH Vercel Analytics and GA4 via `src/utils/analytics.ts`.
 *
 * The `source` prop labels which surface the click came from
 * (e.g. `home_hero`, `footer`, `blog_inline_after_intro`) so per-CTA
 * conversion shows up in GA4 Explore under the `source` dimension.
 *
 * Optional `ctaPosition` and any extra parameters land in the same GA4 event
 * so the dashboard can break down by where on the page the CTA sat.
 */
import { APP_STORE_URL, appStoreHref } from '@/utils/appStore'

// Re-exported for existing client-side imports; server components must
// import from '@/utils/appStore' directly (client-boundary constants don't
// cross into server components as strings).
export { APP_STORE_URL }

export interface AppStoreLinkProps {
  /** Required attribution label. Use a stable snake_case string so events
   *  group cleanly in the analytics dashboard. */
  source: string
  /** Override the destination for custom product pages (?ppid=) or in-app
   *  events (?eventid=). Defaults to the canonical App Store URL. */
  href?: string
  /** Optional CTA position label (`above_fold | mid_article | bottom | sticky`)
   *  for finer-grained dashboard breakdown. */
  ctaPosition?: string
  /** Optional extra event params (post_topic, post_slug, page_type, etc). */
  trackingParams?: EventParams
  className?: string
  children: React.ReactNode
  /** Optional surface-specific click event. The canonical destination event
   *  still fires here after this handler unless it prevents default. */
  onClick?: React.MouseEventHandler<HTMLAnchorElement>
  /** Optional aria-label override for icon-only buttons. */
  'aria-label'?: string
}

export default function AppStoreLink({
  source,
  href,
  ctaPosition,
  trackingParams,
  className,
  children,
  onClick,
  'aria-label': ariaLabel,
}: AppStoreLinkProps) {
  const router = useRouter()
  const linkRef = useRef<HTMLAnchorElement>(null)
  const impressionTrackedRef = useRef(false)

  useEffect(() => {
    const element = linkRef.current
    if (!element || impressionTrackedRef.current) return

    const fire = () => {
      if (impressionTrackedRef.current) return
      impressionTrackedRef.current = true
      trackCtaImpression(source, {
        ...(ctaPosition ? { cta_position: ctaPosition } : {}),
        cta_component: 'app_store_link',
        ...(trackingParams ?? {}),
      })
    }

    if (typeof IntersectionObserver === 'undefined') {
      fire()
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
          fire()
          observer.disconnect()
        }
      },
      { threshold: [0.5] },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [source, ctaPosition, trackingParams])

  return (
    <a
      ref={linkRef}
      href={appStoreHref(source, href)}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      aria-label={ariaLabel}
      onClick={(e) => {
        onClick?.(e)
        if (e.defaultPrevented) return

        // Android users can't install from the iOS App Store. 22 of the
        // last 104 store clicks (21%) were Android and converted to
        // nothing. Route them to the waitlist instead, tracked under a
        // separate event so the store-click numbers stay clean.
        const platform = classifyAppStorePlatform(
          typeof navigator === 'undefined' ? '' : navigator.userAgent,
        )
        if (platform === 'android') {
          e.preventDefault()
          trackAndroidWaitlistRedirect(source, {
            ...(ctaPosition ? { cta_position: ctaPosition } : {}),
            ...(trackingParams ?? {}),
          })
          // router.push, not window.location.href. Next 16's
          // @next/next/no-location-assign-relative-destination flags the latter
          // for internal destinations: it triggers a full document reload,
          // discarding the client router cache and the React tree, which is
          // both slower and loses scroll/state. This is an in-app anchor on the
          // home page, so it belongs to the router.
          router.push('/#android-waitlist')
          return
        }
        trackAppStoreClick(source, {
          ...(ctaPosition ? { cta_position: ctaPosition } : {}),
          ...(trackingParams ?? {}),
        })
      }}
    >
      {children}
    </a>
  )
}
